<template>
    <div class="bg-white rounded-lg border border-gray-200 p-3 overflow-hidden">
        <div class="flex justify-between items-center mb-2">
            <h3 class="text-sm font-semibold text-gray-800">Attendance Record</h3>
            <Button label="Mark Attendance" icon="pi pi-plus" size="small" class="p-button-sm" @click="showMarkDialog = true" />
        </div>

        <div v-if="loading && !attendanceData" class="text-center p-2">
            <i class="pi pi-spin pi-spinner text-sm text-primary"></i>
        </div>

        <div v-else-if="error" class="text-center p-2 text-red-500 text-xs">
            <p>Failed to load attendance records.</p>
        </div>

        <div v-else class="relative">
            <div v-if="loading" class="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                <i class="pi pi-spin pi-spinner text-lg text-primary"></i>
            </div>
            <!-- Summary Metrics -->
            <div class="grid grid-cols-3 gap-2 mb-3">
                <div class="bg-primary/10 p-2 rounded-lg text-center">
                    <div class="text-primary font-semibold text-xs">Present</div>
                    <div class="text-base font-bold text-primary">{{ summary?.present || 0 }}</div>
                </div>
                <div class="bg-red-50 p-2 rounded-lg text-center">
                    <div class="text-red-600 font-semibold text-xs">Absent</div>
                    <div class="text-base font-bold text-red-800">{{ summary?.absent || 0 }}</div>
                </div>
                <div class="bg-orange-50 p-2 rounded-lg text-center">
                    <div class="text-orange-600 font-semibold text-xs">Late</div>
                    <div class="text-base font-bold text-orange-800">{{ summary?.late || 0 }}</div>
                </div>
            </div>

            <!-- Calendar View -->
            <div class="flex justify-center overflow-hidden">
                <VCalendar :attributes="attributes" expanded class="w-full max-w-md border-none shadow-none"
                    @did-move="onCalendarMove" />
            </div>

            <div class="flex gap-3 justify-center mt-2 text-xs text-gray-600">
                <div class="flex items-center gap-1">
                    <div class="w-2.5 h-2.5 rounded-full bg-primary"></div> Present
                </div>
                <div class="flex items-center gap-1">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div> Absent
                </div>
                <div class="flex items-center gap-1">
                    <div class="w-2.5 h-2.5 rounded-full bg-orange-500"></div> Late
                </div>
                <div class="flex items-center gap-1">
                    <div class="w-2.5 h-2.5 rounded-full bg-primary"></div> Excused
                </div>
            </div>
        </div>
    </div>

    <!-- Mark Attendance Dialog -->
    <Dialog v-model:visible="showMarkDialog" header="Mark Attendance" modal class="w-[90%] md:w-[380px]">
        <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold">Date</label>
                <DatePicker v-model="markDate" showIcon fluid dateFormat="dd/mm/yy" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold">Status</label>
                <SelectButton v-model="markStatus" :options="statusOptions" optionLabel="label" optionValue="value" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold">Remark</label>
                <InputText v-model="markRemark" placeholder="Optional" />
            </div>
        </div>
        <template #footer>
            <Button label="Cancel" text size="small" @click="showMarkDialog = false" />
            <Button label="Save" icon="pi pi-check" size="small" @click="saveAttendance" :loading="saving" />
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useToast } from 'vue-toastification';
import type { Student } from '@/types/composition-functions';

import { useMarkStudentAttendanceMutation } from '@/types/vue-apollo-helpers';
import { GET_STUDENT_ATTENDANCE } from '@/graphql';
import { toDateOnly } from '@/utils/dateOnly';

import dayjs from 'dayjs';

const props = defineProps<{
    student: Student
}>();

const toast = useToast();
const showMarkDialog = ref(false);
const markDate = ref(new Date());
const markStatus = ref('PRESENT');
const markRemark = ref('');
const saving = ref(false);

const currentMonth = ref(dayjs().startOf('month'));

const statusOptions = [
  { label: 'Present', value: 'PRESENT' },
  { label: 'Absent', value: 'ABSENT' },
  { label: 'Late', value: 'LATE' },
  { label: 'Excused', value: 'EXCUSED' },
];

// Fetch Attendance Data
const { result, loading, error, refetch } = useQuery(GET_STUDENT_ATTENDANCE, () => ({
  student_id: props.student.id,
  start_date: currentMonth.value.format('YYYY-MM-DD'),
  end_date: currentMonth.value.endOf('month').format('YYYY-MM-DD'),
}), {
  fetchPolicy: 'network-only',
});

const onCalendarMove = (pages: { month: number; year: number }[]) => {
  if (pages && pages.length > 0) {
    const { month, year } = pages[0];
    // VCalendar month is 1-indexed
    currentMonth.value = dayjs(`${year}-${month}-01`);
  }
};

const attendanceData = computed(() => result.value?.studentAttendance);
const records = computed(() => attendanceData.value?.records || []);
const summary = computed(() => attendanceData.value?.summary);

// Map records to VCalendar attributes
const attributes = computed(() => records.value.map((record: { date: string; status: string; remark?: string }) => {
  let color = 'gray';
  if (record.status === 'PRESENT') color = 'green';
  else if (record.status === 'ABSENT') color = 'red';
  else if (record.status === 'LATE') color = 'orange';
  else if (record.status === 'EXCUSED') color = 'blue';

  return {
    key: record.date,
    highlight: {
      color: color,
      fillMode: 'solid',
    },
    dates: new Date(record.date),
    popover: {
      label: `${record.status}${record.remark ? ': ' + record.remark : ''}`,
    },
  };
}));

const { mutate: markAttendanceMutation } = useMarkStudentAttendanceMutation();

const saveAttendance = async() => {
  if (!markDate.value) {
    toast.error('Please select a date');
    return;
  }

  saving.value = true;
  try {
    const formattedDate = toDateOnly(markDate.value);

    const input = [{
      student_id: props.student.id,
      status: markStatus.value,
      date: formattedDate,
      remark: markRemark.value,
    }];

    const response = await markAttendanceMutation({ input });
    const data = response?.data;

    if (data?.markStudentAttendance?.success) {
      toast.success('Attendance marked successfully');
      showMarkDialog.value = false;
      refetch(); // Refresh the list
    } else {
      toast.error(data?.markStudentAttendance?.message || 'Failed to mark attendance');
    }
  } catch (error) {
    toast.error('An error occurred while saving attendance');
  } finally {
    saving.value = false;
  }
};
</script>
